'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Lasso, PencilLine, SquareDashed, X } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { RenderStatus } from '@/shared/constants/status';
import { useBreakpoint } from '@/shared/hooks/use-media-query';
import { useRenderStore } from '../../model/render-form.store';
import { ANGLE_OVERLAY_BUTTON } from '@/features/design/concept/ui/angle-overlay-chrome';
import type { EditRegion, EditRegionShape, Render } from '@/types/render';
import {
  editRegionColor,
  MAX_EDIT_REGIONS,
  coverWindow,
  frameToImage,
  imageToFrame,
  finalizeGesture,
  regionBounds,
  regionPolygon,
  svgPoints,
  type Point,
} from './edit-region-geometry';

/** Max length of one region instruction (server rule). */
const INSTRUCTION_MAX = 200;

interface EditRegionsLayerProps {
  /** Concept angle this image belongs to. */
  conceptAngleId: string;
  /** The render on screen — the parent of the refine the regions belong to. */
  currentRender: Render | undefined;
  /** A render or upscale is running for this view: no editing. */
  isBusy: boolean;
}

/** Region under edit: a fresh gesture (index null) or an existing region. */
interface EditorState {
  index: number | null;
  shape: EditRegionShape;
  points: Point[];
  text: string;
}

/**
 * Mark areas directly on the render and tell the model what to do with each
 * ("make it round", "replace with a lamp"). The regions ride the Refine draft
 * (`formData.edit_regions`), so they persist with it and go out with the next
 * Generate. Picking a tool switches the form to Refine on the render shown.
 *
 * Overlay order inside the image frame: tool pill top-trailing (next to the ⋯
 * menu), region chips bottom, editor anchored under the shape (wide screens)
 * or docked to the image bottom (phones, where a floating field would sit
 * under the finger and the keyboard).
 */
export function EditRegionsLayer({
  conceptAngleId,
  currentRender,
  isBusy,
}: EditRegionsLayerProps) {
  const t = useTranslations('render.editRegions');
  const { isSm } = useBreakpoint();
  const mode = useRenderStore((s) => s.formData.mode);
  const parentRenderId = useRenderStore((s) => s.formData.parent_render_id);
  const draftRegions = useRenderStore((s) => s.formData.edit_regions);
  const updateFormData = useRenderStore((s) => s.updateFormData);
  const setRefineMode = useRenderStore((s) => s.setRefineMode);
  const context = useRenderStore((s) => s.currentContext);

  const [tool, setTool] = useState<EditRegionShape | null>(null);
  const [gesture, setGesture] = useState<Point[] | null>(null);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [showStored, setShowStored] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);
  // Box of the photo itself inside the card. The card can be wider than the
  // photo (letterbox strips beside a tall image), and region coordinates are
  // shares of the PHOTO — so the regions layer is pinned to this box.
  const boxRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);
  // Latest commitEditor for the native listeners below (they outlive renders).
  const commitRef = useRef<() => void>(() => {});
  const inputRef = useRef<HTMLInputElement>(null);

  // The render form holds ONE draft context (the view and render the settings
  // panel is on). Marking is offered only on that image, so a region can never
  // land in another view's draft.
  const canEdit =
    !!currentRender &&
    context?.angleId === conceptAngleId &&
    context?.renderId === currentRender.id &&
    currentRender.attributes.status === RenderStatus.COMPLETED &&
    !isBusy;
  const isOwnDraft =
    mode === 'refine' && !!currentRender && parentRenderId === currentRender.id;
  const regions: EditRegion[] = isOwnDraft ? (draftRegions ?? []) : [];
  const storedRegions = currentRender?.attributes.settings?.edit_regions ?? [];
  const isFull = regions.length >= MAX_EDIT_REGIONS;
  // The frame shows the render cover-cropped to the view's aspect; region
  // points live in the WHOLE image's space, so every frame↔image step goes
  // through this window.
  const win = coverWindow(
    box?.width ?? 0,
    box?.height ?? 0,
    currentRender?.attributes.width,
    currentRender?.attributes.height
  );
  const winRef = useRef(win);
  winRef.current = win;

  // A different render on screen ends any half-done gesture or edit.
  useEffect(() => {
    setTool(null);
    setGesture(null);
    setEditor(null);
    setShowStored(false);
  }, [currentRender?.id]);

  useEffect(() => {
    if (!isBusy) return;
    setTool(null);
    setGesture(null);
    setEditor(null);
  }, [isBusy]);

  useEffect(() => {
    if (editor) inputRef.current?.focus();
  }, [editor]);

  useEffect(() => {
    if (!tool || editor) return;
    const onKey = (e: KeyboardEvent) => {
      // Esc belongs to an open menu or dialog first (the ⋯ menu closes on it);
      // only a bare Esc puts the tool away.
      if (e.key !== 'Escape') return;
      if (document.querySelector('[role="menu"], [role="dialog"]')) return;
      setTool(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [tool, editor]);

  useLayoutEffect(() => {
    const host = boxRef.current?.parentElement;
    const frame = host?.querySelector<HTMLElement>('[data-image-frame]');
    if (!host || !frame) return;
    const measure = () => {
      const h = host.getBoundingClientRect();
      const f = frame.getBoundingClientRect();
      setBox({
        left: f.left - h.left,
        top: f.top - h.top,
        width: f.width,
        height: f.height,
      });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(host);
    observer.observe(frame);
    return () => observer.disconnect();
  }, [canEdit, currentRender?.id]);

  // Drawing listens on the image frame itself (capture phase) instead of a
  // surface laid over it: anything interactive on the image — the ⋯ menu,
  // feedback icons, the compare handle, these tool buttons, the region
  // outlines — keeps working while a tool is active. Only a press on bare
  // image starts a gesture, and the click that follows it is swallowed so the
  // full-screen viewer does not open.
  useEffect(() => {
    const host = boxRef.current?.parentElement;
    if (!host || !tool || isFull) return;

    const isInteractive = (target: EventTarget | null) =>
      target instanceof Element &&
      !!target.closest(
        'button, a, input, [role="button"], [data-image-control]'
      );

    const toPoint = (e: PointerEvent): Point => {
      const rect = svgRef.current!.getBoundingClientRect();
      return frameToImage(
        winRef.current,
        (e.clientX - rect.left) / rect.width,
        (e.clientY - rect.top) / rect.height
      );
    };

    let points: Point[] | null = null;

    const onDown = (e: PointerEvent) => {
      if (isInteractive(e.target)) return;
      // Presses on the letterbox beside the photo draw nothing.
      const r = svgRef.current?.getBoundingClientRect();
      if (
        !r ||
        e.clientX < r.left ||
        e.clientX > r.right ||
        e.clientY < r.top ||
        e.clientY > r.bottom
      ) {
        return;
      }
      // A press on bare image while an instruction field is open saves that
      // instruction (empty → the shape is dropped) and starts the next shape.
      // preventDefault below keeps the input from blurring, so commit here.
      commitRef.current();
      e.preventDefault();
      e.stopPropagation();
      // That commit may have filled the last slot: the effect re-runs on
      // isFull and drops these listeners, so a gesture started now would
      // never see its pointerup and leave a ghost outline behind.
      const saved = useRenderStore.getState().formData.edit_regions ?? [];
      if (saved.length >= MAX_EDIT_REGIONS) return;
      host.setPointerCapture(e.pointerId);
      points = [toPoint(e)];
      setGesture(points);
    };
    const onMove = (e: PointerEvent) => {
      if (!points) return;
      e.stopPropagation();
      const p = toPoint(e);
      const [first] = points;
      const last = points[points.length - 1];
      if (tool === 'rect' && first) {
        points = [first, p];
      } else if (last && Math.hypot(p[0] - last[0], p[1] - last[1]) > 0.004) {
        points = [...points, p];
      } else {
        return;
      }
      setGesture(points);
    };
    const onUp = (e: PointerEvent) => {
      if (!points) return;
      e.stopPropagation();
      const shape = finalizeGesture(tool, points);
      points = null;
      setGesture(null);
      if (shape) setEditor({ index: null, ...shape, text: '' });
    };
    const onCancel = () => {
      points = null;
      setGesture(null);
    };
    // Mouse/touch twins of the pointer events still reach the compare slider
    // (it listens to mousedown/touchstart) — stop them on bare image too.
    const swallow = (e: Event) => {
      if (!isInteractive(e.target)) {
        e.stopPropagation();
        if (e.type === 'click') e.preventDefault();
      }
    };

    const prevTouch = host.style.touchAction;
    host.dataset.regionDrawing = '';
    host.style.touchAction = 'none';
    host.addEventListener('pointerdown', onDown, true);
    host.addEventListener('pointermove', onMove, true);
    host.addEventListener('pointerup', onUp, true);
    host.addEventListener('pointercancel', onCancel, true);
    for (const type of ['mousedown', 'touchstart', 'click'] as const) {
      host.addEventListener(type, swallow, true);
    }
    return () => {
      setGesture(null);
      delete host.dataset.regionDrawing;
      host.style.touchAction = prevTouch;
      host.removeEventListener('pointerdown', onDown, true);
      host.removeEventListener('pointermove', onMove, true);
      host.removeEventListener('pointerup', onUp, true);
      host.removeEventListener('pointercancel', onCancel, true);
      for (const type of ['mousedown', 'touchstart', 'click'] as const) {
        host.removeEventListener(type, swallow, true);
      }
    };
  }, [tool, isFull]);

  if (!canEdit) return null;

  const writeRegions = (next: EditRegion[]) =>
    updateFormData({ edit_regions: next.length ? next : null });

  const pickTool = (shape: EditRegionShape) => {
    if (tool === shape) {
      setTool(null);
      return;
    }
    if (!isOwnDraft && currentRender) {
      setRefineMode('refine', currentRender.id);
    }
    setShowStored(false);
    setTool(shape);
  };

  const commitEditor = () => {
    if (!editor) return;
    const text = editor.text.trim();
    const next = [...regions];
    if (editor.index === null) {
      if (text)
        next.push({
          shape: editor.shape,
          points: editor.points,
          instruction: text,
        });
    } else if (text) {
      next[editor.index] = {
        shape: editor.shape,
        points: editor.points,
        instruction: text,
      };
    } else {
      next.splice(editor.index, 1);
    }
    writeRegions(next);
    setEditor(null);
  };

  commitRef.current = commitEditor;

  const openRegion = (index: number) => {
    const region = regions[index];
    if (!region) return;
    setEditor({
      index,
      shape: region.shape,
      points: region.points,
      text: region.instruction,
    });
  };

  const removeRegion = (index: number) => {
    writeRegions(regions.filter((_, i) => i !== index));
    if (editor?.index === index) setEditor(null);
  };

  // Colour of the region under edit: a new one takes the next slot.
  const nextColor = editRegionColor(regions.length);
  const editorColor = editor
    ? editRegionColor(editor.index ?? regions.length)
    : null;

  const visibleRegions: {
    region: EditRegion;
    color: string;
    key: string;
    /** Draft index (tappable), null for a finished render's read-only areas. */
    index: number | null;
  }[] =
    regions.length || tool || editor
      ? regions.map((region, i) => ({
          region,
          color: editRegionColor(i),
          key: `d${i}`,
          index: i,
        }))
      : showStored
        ? storedRegions.map((region, i) => ({
            region,
            color: region.color,
            key: `s${i}`,
            index: null,
          }))
        : [];

  // Field placement is in frame space (what is on screen), not image space.
  const editorBounds = editor
    ? regionBounds(regionPolygon(editor).map((p) => imageToFrame(win, p)))
    : null;
  // Wide screens: the field sits under the shape, or above it near the bottom.
  const editorBelow = editorBounds ? editorBounds.maxY < 0.72 : true;

  const editorField = editor && (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        commitEditor();
      }}
      onPointerDown={(e) => e.stopPropagation()}
      data-image-control
      className={cn(
        'pointer-events-auto z-30 flex items-center gap-2 rounded-full border border-border bg-background/95 py-1 ps-3 pe-1 shadow-lg backdrop-blur-sm',
        isSm ? 'absolute w-72' : 'absolute inset-x-2 bottom-2'
      )}
      style={
        isSm && editorBounds
          ? {
              left: `clamp(8px, ${editorBounds.minX * 100}%, calc(100% - 296px))`,
              ...(editorBelow
                ? { top: `calc(${editorBounds.maxY * 100}% + 8px)` }
                : { bottom: `calc(${(1 - editorBounds.minY) * 100}% + 8px)` }),
            }
          : undefined
      }
    >
      <span
        aria-hidden
        className="h-3 w-3 shrink-0 rounded-full"
        style={{ backgroundColor: editorColor ?? undefined }}
      />
      <input
        ref={inputRef}
        value={editor.text}
        maxLength={INSTRUCTION_MAX}
        placeholder={t('placeholder')}
        aria-label={t('placeholder')}
        onChange={(e) => setEditor({ ...editor, text: e.target.value })}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            setEditor(null);
          }
        }}
        onBlur={commitEditor}
        className="min-w-0 flex-1 bg-transparent text-settings-row-title text-foreground outline-none placeholder:text-muted-foreground"
      />
      <button
        type="submit"
        onMouseDown={(e) => e.preventDefault()}
        className="h-8 shrink-0 rounded-full bg-primary px-3 text-settings-row-title font-semibold text-primary-foreground"
      >
        {t('done')}
      </button>
    </form>
  );

  return (
    <>
      {/* Everything that sits ON the photo lives in this box, pinned to the
          photo's own rect (see `box`). */}
      <div
        ref={boxRef}
        className="pointer-events-none absolute z-20"
        style={box ?? { inset: 0 }}
      >
        {/* Regions. The outlines of the draft are tappable: a tap opens that
          region's instruction, like its chip below. */}
        <svg
          ref={svgRef}
          viewBox={`${win.x} ${win.y} ${win.w} ${win.h}`}
          preserveAspectRatio="none"
          className="pointer-events-none absolute inset-0 z-20 h-full w-full"
          aria-hidden
        >
          {visibleRegions.map(({ region, color, key, index }) => (
            <polygon
              key={key}
              data-image-control
              className={
                index !== null && !editor
                  ? 'pointer-events-auto cursor-pointer'
                  : undefined
              }
              onClick={(e) => {
                if (index === null || editor) return;
                e.stopPropagation();
                openRegion(index);
              }}
              points={svgPoints(regionPolygon(region))}
              fill={`${color}47`}
              stroke={color}
              strokeWidth={2.5}
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {(gesture || editor) && (
            <polygon
              points={svgPoints(
                editor
                  ? regionPolygon(editor)
                  : tool === 'rect' && gesture && gesture.length === 2
                    ? regionPolygon({ shape: 'rect', points: gesture })
                    : (gesture ?? [])
              )}
              fill={`${editorColor ?? nextColor}47`}
              stroke={editorColor ?? nextColor}
              strokeWidth={2.5}
              strokeDasharray={editor ? undefined : '6 4'}
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          )}
        </svg>

        {/* Region chips (draft) — one scrollable row on the image bottom. */}
        {regions.length > 0 && !(editor && !isSm) && (
          <div className="pointer-events-auto absolute inset-x-0 bottom-0 z-30 flex gap-1.5 overflow-x-auto p-2 [scrollbar-width:none]">
            {regions.map((region, i) => (
              <div
                key={i}
                className="flex max-w-[16rem] shrink-0 items-center gap-1.5 rounded-full bg-background/90 py-1 ps-2.5 pe-1 shadow-sm backdrop-blur-sm"
              >
                <span
                  aria-hidden
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: editRegionColor(i) }}
                />
                <button
                  type="button"
                  onClick={() => openRegion(i)}
                  className="truncate text-chip text-foreground"
                >
                  {region.instruction}
                </button>
                <button
                  type="button"
                  aria-label={t('remove')}
                  onClick={() => removeRegion(i)}
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Read-only labels of a finished render's areas, while revealed. */}
        {showStored && regions.length === 0 && !tool && (
          <div className="pointer-events-auto absolute inset-x-0 bottom-0 z-30 flex gap-1.5 overflow-x-auto p-2 pe-14 [scrollbar-width:none]">
            {storedRegions.map((region, i) => (
              <div
                key={i}
                className="flex max-w-[16rem] shrink-0 items-center gap-1.5 rounded-full bg-background/90 px-2.5 py-1 shadow-sm backdrop-blur-sm"
              >
                <span
                  aria-hidden
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: region.color }}
                />
                <span className="truncate text-chip text-foreground">
                  {region.instruction}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Finished render made with marked areas: a badge reveals them. */}
        {storedRegions.length > 0 && regions.length === 0 && !tool && (
          <button
            type="button"
            aria-pressed={showStored}
            aria-label={t('editsBadge')}
            title={t('editsBadge')}
            // Wide screens reveal on hover; a click there must not undo it.
            onClick={() => setShowStored((v) => (isSm ? true : !v))}
            onMouseEnter={() => isSm && setShowStored(true)}
            onMouseLeave={() => isSm && setShowStored(false)}
            className={cn(
              'pointer-events-auto absolute bottom-2 end-2 z-30 flex h-7 items-center gap-1 rounded-full px-2.5 text-chip',
              showStored
                ? 'bg-primary text-primary-foreground'
                : 'bg-background/90 text-foreground shadow-sm backdrop-blur-sm'
            )}
          >
            <PencilLine className="h-3.5 w-3.5" />
            {storedRegions.length}
          </button>
        )}

        {editorField}
      </div>

      {/* Tool column — under the ⋯ menu (top-trailing 44px), clear of the
          derivative cards' variant pager that shares the top row. */}
      <div className="absolute top-11 end-0 z-30 flex flex-col items-center">
        {/* Only the rectangle shows at rest; the lasso joins it once a tool is
            picked, so an idle image carries a single icon. */}
        {(tool ? (['rect', 'lasso'] as const) : (['rect'] as const)).map(
          (shape) => {
            const Icon = shape === 'rect' ? SquareDashed : Lasso;
            const label = shape === 'rect' ? t('rectTool') : t('lassoTool');
            const active = tool === shape;
            return (
              <button
                key={shape}
                type="button"
                aria-pressed={active}
                aria-label={label}
                title={
                  isFull && !active
                    ? t('limitReached', { max: MAX_EDIT_REGIONS })
                    : label
                }
                disabled={isFull && !active}
                onClick={() => pickTool(shape)}
                className={cn(
                  'relative inline-flex h-11 w-11 items-center justify-center disabled:opacity-50',
                  "before:absolute before:inset-2 before:rounded-full before:content-['']",
                  active
                    ? 'before:bg-primary [&_svg]:text-primary-foreground'
                    : ANGLE_OVERLAY_BUTTON
                )}
              >
                <Icon className="relative h-4 w-4" />
              </button>
            );
          }
        )}
      </div>
    </>
  );
}
